/**
 * HotelResultsPage — themed to match BookingPage.jsx (flight booking).
 *
 * Theme conventions (mirrored from /app/frontend/src/pages/BookingPage.jsx):
 *   - Page bg: gradient-to-br from-blue-50 via-white to-teal-50
 *   - Section cards: bg-white rounded-2xl shadow-sm border border-gray-200
 *     overflow-hidden + `bg-gradient-to-r from-[#1B375B] to-[#659A9B]` section
 *     header with white bold heading
 *   - Auth chip top-right with gradient avatar
 *
 * UX additions:
 *   - Inline Modify-search panel (HotelSearchForm) opens on the same page;
 *     no redirect to home anymore. Submitting it re-runs the search and
 *     replaces the listing in place.
 *   - View toggle (List/Grid), client-side filter+sort+pagination on the
 *     cached supplier response (raised page_size cap = 1000 in backend).
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, ArrowLeft, Building2, Search as SearchIcon, SlidersHorizontal,
  X, ArrowUpDown, LayoutGrid, LayoutList, Calendar, Users, MapPin, Edit3, Star,
  User, Wallet, LogOut, LogIn, ChevronUp,
} from 'lucide-react';
import Footer from '../components/Footer';
import BottomNav from '../components/BottomNav';
import HotelFiltersSidebar, { _cityOf, _localityOf } from '../components/hotel/HotelFiltersSidebar';
import HotelCardItem, { HotelCardSkeleton } from '../components/hotel/HotelCardItem';
import HotelSearchForm from '../components/hotel/HotelSearchForm';
import { useAgentAuth } from '../context/AgentAuthContext';
import { useAuth } from '../context/AuthContext';
import AgentBalanceBadge from '../components/agent/AgentBalanceBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FETCH_SIZE = 500;
const PAGE_SIZE = 12;

const SORT_OPTIONS = [
  { id: 'recommended', label: 'Recommended' },
  { id: 'price_asc', label: 'Price: Low → High' },
  { id: 'price_desc', label: 'Price: High → Low' },
  { id: 'rating_desc', label: 'Rating: High → Low' },
  { id: 'name_asc', label: 'Name: A → Z' },
];

const _emptyFilters = () => ({
  query: '', stars: [], refundableOnly: false, amenities: [],
  cities: [], localities: [], priceMin: null, priceMax: null,
});

const _parseRooms = (rawParam) => {
  if (!rawParam) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(rawParam));
    if (!Array.isArray(parsed)) return null;
    let adults = 0, children = 0;
    for (const r of parsed) {
      adults += (r.numberOfAdults || 0);
      children += (r.numberOfChild || 0);
    }
    return { rooms: parsed.length, adults, children };
  } catch { return null; }
};

const _fmtDateBadge = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', weekday: 'short' });
  } catch { return iso; }
};

const _nightsBetween = (a, b) => {
  if (!a || !b) return 0;
  try {
    const ms = new Date(b) - new Date(a);
    return Math.max(0, Math.round(ms / (1000 * 60 * 60 * 24)));
  } catch { return 0; }
};

const FilterChip = ({ label, onRemove, testId }) => (
  <span
    className="inline-flex items-center gap-1 pl-3 pr-1 py-1 bg-gradient-to-r from-[#1B375B] to-[#659A9B] text-white text-xs font-bold rounded-full shadow-sm"
    data-testid={testId}
  >
    {label}
    <button type="button" onClick={onRemove} className="ml-0.5 w-5 h-5 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center transition-colors" aria-label={`Remove ${label}`}>
      <X className="w-3 h-3" />
    </button>
  </span>
);

const HotelResultsPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('sessionId');
  const cityName = params.get('cityname') || 'your search';
  const checkin = params.get('checkin');
  const checkout = params.get('checkout');
  const roomsInfo = useMemo(() => _parseRooms(params.get('rooms')), [params]);
  const nights = _nightsBetween(checkin, checkout);

  const { agent, isAuthenticated: isAgent, logout: agentLogout, refreshAgentBalances } = useAgentAuth();
  const { isAuthenticated: isB2C, user: b2cUser, logout: b2cLogout } = useAuth();

  useEffect(() => {
    if (!agent?.id) return;
    refreshAgentBalances();
  }, [agent?.id]);

  const [hotels, setHotels] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [totalSupplier, setTotalSupplier] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const [filters, setFilters] = useState(_emptyFilters());
  const [sort, setSort] = useState('recommended');
  const [view, setView] = useState(() => (typeof window !== 'undefined' && window.innerWidth < 768) ? 'grid' : 'list');
  const [page, setPage] = useState(1);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [modifyOpen, setModifyOpen] = useState(false);

  const pollAbort = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setError('Search session missing. Please go back and start a new search.');
      setLoading(false);
      return;
    }
    pollAbort.current = false;
    setLoading(true);
    setHotels([]); setError('');
    (async () => {
      try {
        const res = await axios.get(
          `${BACKEND_URL}/api/hotels/results/${encodeURIComponent(sessionId)}?page=1&page_size=${FETCH_SIZE}`,
          { timeout: 120000 },
        );
        if (pollAbort.current) return;
        const data = res.data || {};
        if (!data.success) {
          setError(data.error?.message || 'Hotel results are temporarily unavailable.');
          setLoading(false);
          return;
        }
        setHotels(data.hotels || []);
        setCompleted(!!data.completed);
        setTotalSupplier(data.total || 0);
      } catch (e) {
        if (pollAbort.current) return;
        setError(e.response?.data?.detail?.message || 'Network error. Please retry.');
      } finally {
        setLoading(false);
      }
    })();
    return () => { pollAbort.current = true; };
  }, [sessionId]);

  const filteredHotels = useMemo(() => {
    const q = filters.query.trim().toLowerCase();
    let out = hotels.filter((h) => {
      if (q && !(h.name || '').toLowerCase().includes(q)) return false;
      if (filters.stars.length > 0) {
        const r = Math.round(h.rating || 0);
        if (!filters.stars.includes(r)) return false;
      }
      if (filters.refundableOnly && h.refundable !== true) return false;
      if (filters.priceMax !== null && (h.starting_price ?? 0) > filters.priceMax) return false;
      if (filters.priceMin !== null && (h.starting_price ?? 0) < filters.priceMin) return false;
      if (filters.amenities.length > 0) {
        const has = new Set((h.amenities || []).map((a) => String(a)));
        for (const need of filters.amenities) if (!has.has(need)) return false;
      }
      if ((filters.cities || []).length > 0 && !filters.cities.includes(_cityOf(h.address))) return false;
      if ((filters.localities || []).length > 0 && !filters.localities.includes(_localityOf(h.address))) return false;
      return true;
    });
    switch (sort) {
      case 'price_asc': out = [...out].sort((a, b) => (a.starting_price ?? Infinity) - (b.starting_price ?? Infinity)); break;
      case 'price_desc': out = [...out].sort((a, b) => (b.starting_price ?? -Infinity) - (a.starting_price ?? -Infinity)); break;
      case 'rating_desc': out = [...out].sort((a, b) => (b.rating ?? 0) - (a.rating ?? 0)); break;
      case 'name_asc': out = [...out].sort((a, b) => (a.name || '').localeCompare(b.name || '')); break;
      default: break;
    }
    return out;
  }, [hotels, filters, sort]);

  useEffect(() => { setPage(1); }, [filters, sort]);

  const totalFiltered = filteredHotels.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const visibleHotels = filteredHotels.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const resetFilters = () => setFilters(_emptyFilters());

  const chips = [];
  if (filters.query) chips.push({ key: 'query', label: `"${filters.query}"`, remove: () => setFilters((f) => ({ ...f, query: '' })) });
  for (const s of filters.stars) chips.push({ key: `s${s}`, label: `${s}★`, remove: () => setFilters((f) => ({ ...f, stars: f.stars.filter((x) => x !== s) })) });
  for (const c of (filters.cities || [])) chips.push({ key: `c${c}`, label: c, remove: () => setFilters((f) => ({ ...f, cities: f.cities.filter((x) => x !== c) })) });
  for (const l of (filters.localities || [])) chips.push({ key: `l${l}`, label: l, remove: () => setFilters((f) => ({ ...f, localities: f.localities.filter((x) => x !== l) })) });
  for (const a of filters.amenities) chips.push({ key: `a${a}`, label: a, remove: () => setFilters((f) => ({ ...f, amenities: f.amenities.filter((x) => x !== a) })) });
  if (filters.refundableOnly) chips.push({ key: 'r', label: 'Free Cancellation', remove: () => setFilters((f) => ({ ...f, refundableOnly: false })) });
  if (filters.priceMax !== null) chips.push({ key: 'pmax', label: `≤ ₹${filters.priceMax.toLocaleString('en-IN')}`, remove: () => setFilters((f) => ({ ...f, priceMax: null })) });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 pb-20 md:pb-12" data-testid="hotel-results-page">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">

        {/* ── Back + Auth chip (mirrors flight booking) ── */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-5">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-all shadow-sm hover:shadow-md"
            data-testid="hotel-results-back"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </button>
          {isAgent && agent ? (
            <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-2 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-teal-500 to-cyan-600 flex items-center justify-center">
                  <Wallet className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Agent</p>
                  <p className="text-sm font-bold text-gray-800">{agent.full_name}</p>
                </div>
              </div>
              <AgentBalanceBadge agent={agent} variant="compact" />
              <button onClick={agentLogout} className="text-xs text-red-600 hover:text-red-700 font-semibold flex items-center gap-1 ml-2">
                <LogOut className="w-3 h-3" /> Logout
              </button>
            </div>
          ) : isB2C && b2cUser ? (
            <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-2 shadow-sm">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#1B375B] to-[#659A9B] flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-800">{b2cUser.full_name}</p>
                  <p className="text-xs text-gray-500">{b2cUser.phone ? `+91 ${b2cUser.phone}` : b2cUser.email}</p>
                </div>
              </div>
              <button onClick={b2cLogout} className="text-xs text-red-600 hover:text-red-700 font-semibold flex items-center gap-1">
                <LogOut className="w-3 h-3" /> Logout
              </button>
            </div>
          ) : (
            <button
              onClick={() => navigate('/login')}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1B375B] to-[#659A9B] text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              data-testid="hotel-results-login-cta"
            >
              <LogIn className="w-4 h-4" /> Login
            </button>
          )}
        </div>

        {/* ── Search summary card (themed like flight Booking Details) ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden mb-5">
          <div className="bg-gradient-to-r from-[#1B375B] to-[#659A9B] px-6 py-4 flex items-center justify-between">
            <h2 className="text-white font-bold text-base sm:text-lg" data-testid="hotel-results-cityname">Hotels in {cityName}</h2>
            <button
              type="button"
              onClick={() => setModifyOpen((o) => !o)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 text-white rounded-lg text-xs font-extrabold transition-colors"
              data-testid="hotel-results-modify"
            >
              <Edit3 className="w-3.5 h-3.5" /> {modifyOpen ? 'Close' : 'Modify Search'} {modifyOpen ? <ChevronUp className="w-3 h-3" /> : null}
            </button>
          </div>
          <div className="px-6 py-3 grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#659A9B] shrink-0" />
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Destination</p>
                <p className="font-extrabold text-[#1B375B] truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{cityName}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#659A9B] shrink-0" />
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Stay · {nights} {nights === 1 ? 'night' : 'nights'}</p>
                <p className="font-bold text-[#1B375B] truncate">{_fmtDateBadge(checkin)} → {_fmtDateBadge(checkout)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-[#659A9B] shrink-0" />
              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold text-gray-500">Guests · Rooms</p>
                <p className="font-bold text-[#1B375B] truncate">
                  {roomsInfo ? `${roomsInfo.adults} adult${roomsInfo.adults !== 1 ? 's' : ''}${roomsInfo.children ? ` · ${roomsInfo.children} child` : ''} · ${roomsInfo.rooms} room${roomsInfo.rooms !== 1 ? 's' : ''}` : '—'}
                </p>
              </div>
            </div>
          </div>
          {/* Inline Modify panel — uses the same HotelSearchForm component
              that lives on Home; submitting it navigates to a fresh
              /hotels/results URL which triggers a re-fetch. */}
          {modifyOpen && (
            <div className="border-t border-gray-100 px-3 sm:px-6 py-4 bg-gradient-to-br from-blue-50/40 to-teal-50/40" data-testid="hotel-results-modify-panel">
              <HotelSearchForm variant="desktop" />
            </div>
          )}
        </div>

        {/* ── Body ── */}
        {loading && (
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 sm:gap-5">
            <div className="hidden lg:block bg-white rounded-2xl border border-gray-200 p-4 animate-pulse space-y-3">
              {Array.from({ length: 6 }, (_, i) => <div key={i} className="h-3 bg-gray-200 rounded" />)}
            </div>
            <div className="space-y-3">
              {Array.from({ length: 4 }, (_, i) => <HotelCardSkeleton key={i} view={view} />)}
            </div>
          </div>
        )}

        {!loading && error && (
          <div className="bg-white rounded-2xl shadow-sm border border-red-200 overflow-hidden" data-testid="hotel-results-error">
            <div className="bg-gradient-to-r from-rose-500 to-rose-600 px-6 py-3">
              <h3 className="text-white font-bold">Search Issue</h3>
            </div>
            <div className="p-6 text-center">
              <p className="text-red-700 font-semibold mb-3">{error}</p>
              <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1B375B] to-[#659A9B] text-white rounded-lg text-sm font-bold">
                <SearchIcon className="w-4 h-4" /> New Search
              </button>
            </div>
          </div>
        )}

        {!loading && !error && hotels.length === 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-10 text-center" data-testid="hotel-results-empty">
            <Building2 className="w-12 h-12 text-[#1B375B]/30 mx-auto mb-3" />
            <h2 className="text-lg font-bold text-[#1B375B] mb-1">No hotels found</h2>
            <p className="text-sm text-gray-500">Try a different city or dates.</p>
          </div>
        )}

        {!loading && !error && hotels.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4 sm:gap-5">
            <div className="hidden lg:block">
              <HotelFiltersSidebar hotels={hotels} filters={filters} setFilters={setFilters} onReset={resetFilters} total={totalFiltered} />
            </div>

            <div className="min-w-0">
              <div className="bg-white rounded-2xl border border-gray-200 px-3 sm:px-4 py-2.5 flex items-center gap-2 mb-3 shadow-sm" data-testid="hotel-results-toolbar">
                <button
                  type="button"
                  onClick={() => setMobileFiltersOpen(true)}
                  className="lg:hidden inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-gradient-to-r from-[#1B375B] to-[#659A9B] text-white rounded-lg text-xs font-bold relative"
                  data-testid="hotel-results-mobile-filters-open"
                >
                  <SlidersHorizontal className="w-3.5 h-3.5" /> Filters
                  {chips.length > 0 && <span className="absolute -top-1 -right-1 px-1.5 py-0.5 rounded-full bg-amber-400 text-[#1B375B] text-[9px] font-extrabold">{chips.length}</span>}
                </button>
                <p className="text-xs text-gray-600 flex-1 truncate" data-testid="hotel-results-shown">
                  <span className="font-extrabold text-[#1B375B] tabular-nums">{totalFiltered.toLocaleString('en-IN')}</span> of {totalSupplier.toLocaleString('en-IN')} {totalFiltered === 1 ? 'hotel' : 'hotels'}
                </p>
                <div className="hidden sm:flex items-center bg-gray-100 rounded-lg p-0.5">
                  <button type="button" onClick={() => setView('list')} className={`p-1.5 rounded ${view === 'list' ? 'bg-white shadow-sm text-[#1B375B]' : 'text-gray-400'}`} title="List view" data-testid="hotel-results-view-list">
                    <LayoutList className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => setView('grid')} className={`p-1.5 rounded ${view === 'grid' ? 'bg-white shadow-sm text-[#1B375B]' : 'text-gray-400'}`} title="Grid view" data-testid="hotel-results-view-grid">
                    <LayoutGrid className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex items-center gap-1.5">
                  <ArrowUpDown className="w-3.5 h-3.5 text-gray-400" />
                  <select value={sort} onChange={(e) => setSort(e.target.value)} className="text-xs font-bold text-[#1B375B] bg-transparent focus:outline-none cursor-pointer" data-testid="hotel-results-sort">
                    {SORT_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                  </select>
                </div>
              </div>

              {chips.length > 0 && (
                <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="hotel-results-active-chips">
                  {chips.map((c) => <FilterChip key={c.key} label={c.label} onRemove={c.remove} testId={`hotel-results-chip-${c.key}`} />)}
                  <button type="button" onClick={resetFilters} className="text-[11px] font-bold text-[#659A9B] hover:underline ml-1" data-testid="hotel-results-clear-all-chips">Clear all</button>
                </div>
              )}

              {!completed && (
                <p className="text-[11px] text-amber-600 font-semibold mb-3 flex items-center gap-1.5">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Search is still in progress — partial results shown.
                </p>
              )}

              {totalFiltered === 0 ? (
                <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center" data-testid="hotel-results-filtered-empty">
                  <SearchIcon className="w-12 h-12 text-[#1B375B]/30 mx-auto mb-3" />
                  <h2 className="text-lg font-bold text-[#1B375B] mb-1">No hotels match your filters</h2>
                  <p className="text-sm text-gray-500 mb-3">Try removing some filters to see more options.</p>
                  <button type="button" onClick={resetFilters} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-[#1B375B] to-[#659A9B] text-white rounded-lg text-xs font-bold">Reset Filters</button>
                </div>
              ) : (
                <>
                  <div className={view === 'list' ? 'space-y-3' : 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4'}>
                    {visibleHotels.map((h) => (
                      <HotelCardItem
                        key={h.id}
                        hotel={h}
                        sessionId={sessionId}
                        forwardParams={params.toString()}
                        view={view}
                        cityName={_cityOf(h.address)}
                        locality={_localityOf(h.address)}
                      />
                    ))}
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-2 mt-6">
                      <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 text-sm rounded-lg bg-white border border-gray-200 disabled:opacity-50 hover:border-[#1B375B]" data-testid="hotel-results-prev-page">← Prev</button>
                      <span className="text-xs text-gray-600 font-semibold">Page {page} of {totalPages}</span>
                      <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1.5 text-sm rounded-lg bg-white border border-gray-200 disabled:opacity-50 hover:border-[#1B375B]" data-testid="hotel-results-next-page">Next →</button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile filter drawer */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="hotel-results-mobile-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileFiltersOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-[85vw] max-w-[340px] bg-gradient-to-br from-blue-50 via-white to-teal-50 overflow-y-auto p-3 shadow-xl">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-extrabold text-[#1B375B]" style={{ fontFamily: 'Outfit, sans-serif' }}>Filters</h3>
              <button type="button" onClick={() => setMobileFiltersOpen(false)} className="p-1.5 rounded-full bg-white shadow-sm" data-testid="hotel-results-mobile-filters-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <HotelFiltersSidebar hotels={hotels} filters={filters} setFilters={setFilters} onReset={resetFilters} total={totalFiltered} />
            <button type="button" onClick={() => setMobileFiltersOpen(false)} className="w-full mt-3 py-2.5 bg-gradient-to-r from-[#1B375B] to-[#659A9B] text-white rounded-xl text-sm font-bold" data-testid="hotel-results-mobile-filters-apply">
              Show {totalFiltered} hotel{totalFiltered === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      )}

      <Footer />
      <BottomNav />
    </div>
  );
};

export default HotelResultsPage;
